import { render } from "preact";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BuyAgainWidget } from "../components/BuyAgainWidget";
import { CartProvider } from "../context/CartContext";
import { PortalProvider } from "../context/PortalContext";
import { widgetEventBus } from "../lib/widgetEvents";
import { initPostHog } from "../lib/posthog";
import { initProductVisitTracker } from "../lib/productVisitTracker";
import { initFillsUpsell } from "../cart-upsell";
import styles from "../index.css?inline";
import "../index.css";

const WIDGET_HOST_ID = "social-ocean-buy-again-host";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function mountBuyAgainWidget(container: HTMLElement) {
  if (document.getElementById(WIDGET_HOST_ID)) {
    return;
  }

  initPostHog();
  initProductVisitTracker();
  initFillsUpsell();

  const host = document.createElement("div");
  host.id = WIDGET_HOST_ID;

  host.style.position = "fixed";
  host.style.top = "0";
  host.style.left = "0";
  host.style.width = "0";
  host.style.height = "0";
  // Above Pure Rose's sticky header (999) so the panel isn't covered, but
  // below Klaviyo popups (90000) and Rebuy's cart (~2^31) so add-to-cart from
  // the panel still opens the cart on top.
  host.style.zIndex = "1000";

  container.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });

  const rootWrapper = document.createElement("div");
  rootWrapper.className = "so-widget-root";
  rootWrapper.style.isolation = "isolate";
  shadow.appendChild(rootWrapper);

  try {
    const styleSheet = new CSSStyleSheet();
    styleSheet.replaceSync(styles);
    shadow.adoptedStyleSheets = [styleSheet];
  } catch (e) {
    console.error(
      "Failed to construct CSSStyleSheet, falling back to <style>",
      e
    );
    const style = document.createElement("style");
    style.textContent = styles;
    shadow.appendChild(style);
  }

  render(
    <QueryClientProvider client={queryClient}>
      <CartProvider>
        <PortalProvider container={rootWrapper}>
          <BuyAgainWidget />
        </PortalProvider>
      </CartProvider>
    </QueryClientProvider>,
    rootWrapper
  );

  window.addEventListener("social-ocean:open-buy-again", ((e: CustomEvent) => {
    widgetEventBus.emit({
      type: "open",
      tab: e.detail?.tab ?? "manage",
      source: "banner",
    });
  }) as EventListener);

  const cfg = window.__SOCIAL_OCEAN_BUY_AGAIN__;
  if (cfg?.autoOpen) {
    cfg.autoOpen = false;
    widgetEventBus.emit({
      type: "open",
      tab: cfg.defaultPanelTab === "buy-again" ? "buy-again" : "manage",
      source: "url",
    });
  }
}

if (typeof window !== "undefined") {
  const init = () => {
    if (!document.getElementById(WIDGET_HOST_ID)) {
      mountBuyAgainWidget(document.body);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}
